/**
 * StepwiseImport — 템플릿 ZIP import 의 단계별(step-wise) 검증 페이지.
 *
 * 진입: `TemplateImportV2Modal` 에서 "단계별 모드" 로 시작 → init 호출 →
 * `navigate('/wedding/templates/import/stepwise', { state: StepwiseNavState })`.
 *
 * 새로고침 / 직접 진입 시엔 location.state 가 없어 목록으로 리다이렉트.
 * 세션 상태는 메모리만 — 영속화 없음.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useToast } from '../components/ToastContainer'
import { Button } from '../ui/Button'
import { Spinner } from '../ui/Spinner'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import {
  useStepwiseImport,
  type StepwiseSession,
  type LlmPayloadPreview,
} from './template-import/useStepwiseImport'
import { STAGE_META, STAGE_ORDER, prevStage, type StageName } from './template-import/stages'
import { StageList } from './template-import/StageList'
import { StageHtmlEditor } from './template-import/StageHtmlEditor'
import { StageDiffView } from './template-import/StageDiffView'
import { StagePreview } from './template-import/StagePreview'
import { StageMetaPanel } from './template-import/StageMetaPanel'
import { StageDebugPanel } from './template-import/StageDebugPanel'
import { LlmPayloadModal } from './template-import/LlmPayloadModal'
import { useApiFetch } from '../hooks/useApiFetch'

type Tab = 'html' | 'diff' | 'preview' | 'meta' | 'debug'

export function StepwiseImport() {
  const location = useLocation()
  const navigate = useNavigate()
  const { addToast } = useToast()

  const initialSession = location.state as StepwiseSession | null

  // 세션 검증 — sessionId 는 필수, zipS3Key/templateId 중 하나는 있어야 함.
  const isValid =
    !!initialSession &&
    !!initialSession.sessionId &&
    (!!initialSession.zipS3Key || !!initialSession.templateId)

  useEffect(() => {
    if (!isValid) {
      addToast({ type: 'info', message: '단계별 import 세션이 없어 목록으로 돌아갑니다' })
      navigate('/wedding/templates', { replace: true })
    }
    // 의도적으로 초기 1회만.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isValid) return null

  return <StepwiseImportInner session={initialSession!} />
}

function StepwiseImportInner({ session }: { session: StepwiseSession }) {
  const navigate = useNavigate()
  const hook = useStepwiseImport(session)
  const [tab, setTab] = useState<Tab>('html')
  const [confirmRerun, setConfirmRerun] = useState<StageName | null>(null)
  const [committing, setCommitting] = useState(false)
  // LLM payload 미리보기 모달 상태.
  const [llmModal, setLlmModal] = useState<{
    open: boolean
    target: StageName | null
    invalidateAfter: boolean
    preview: LlmPayloadPreview | null
  }>({ open: false, target: null, invalidateAfter: false, preview: null })

  // LLM payload 모달의 모델 셀렉터용 — TemplateEditor 와 동일 엔드포인트 재사용.
  const { data: modelsData } = useApiFetch<{ models: Array<{ id: string; displayName: string }> }>(
    '/api/admin/templates/text-models',
  )
  const textModels = modelsData?.models ?? []

  const onLlmModelChange = (model: string) => {
    hook.setLlmModel(model || undefined)
    if (llmModal.target) setLlmModal((m) => ({ ...m, preview: null }))
  }

  // 모델 변경으로 hook.llmModel 이 갱신되면 payload 재조회 (maxTokens 가 Opus/Sonnet 별로 다름).
  // open 시 첫 fetch 는 triggerNext/triggerRerun 에서 별도로 수행하므로 mount 직후는 skip.
  const initialModelRef = useRef<string | undefined>(hook.llmModel)
  useEffect(() => {
    if (!llmModal.open || !llmModal.target) return
    if (hook.llmModel === initialModelRef.current) return
    initialModelRef.current = hook.llmModel
    if (hook.running === llmModal.target) return
    let cancelled = false
    ;(async () => {
      const preview = await hook.previewLlmPayload(llmModal.target!)
      if (!cancelled) setLlmModal((m) => ({ ...m, preview }))
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hook.llmModel])

  const selected = hook.selected
  const snapshot = hook.snapshotAt(selected)
  const html = hook.htmlAt(selected)
  const prev = prevStage(selected)
  const prevHtml = prev ? hook.htmlAt(prev) : ''

  const hasLaterSnapshot = useMemo(() => {
    const idx = STAGE_ORDER.indexOf(selected)
    for (let i = idx + 1; i < STAGE_ORDER.length; i++) {
      if (hook.snapshotAt(STAGE_ORDER[i])) return true
    }
    return false
  }, [selected, hook])

  const onRerunClick = () => {
    if (hasLaterSnapshot) setConfirmRerun(selected)
    else triggerRerun(selected)
  }

  // LLM stage 는 실행 전에 payload 미리보기 모달을 거친다.
  const triggerNext = async () => {
    const next = hook.pendingNextStage
    if (!next) return
    if (next === 'llm' && !hook.skipLlm) {
      setLlmModal({ open: true, target: next, invalidateAfter: false, preview: null })
      const preview = await hook.previewLlmPayload(next)
      setLlmModal((m) => ({ ...m, preview }))
    } else {
      hook.runNextStage()
    }
  }

  const triggerRerun = async (stage: StageName) => {
    if (stage === 'llm' && !hook.skipLlm) {
      setLlmModal({ open: true, target: stage, invalidateAfter: true, preview: null })
      const preview = await hook.previewLlmPayload(stage)
      setLlmModal((m) => ({ ...m, preview }))
    } else {
      hook.rerunFromStage(stage)
    }
  }

  const onLlmConfirm = (editedSystem: string) => {
    if (!llmModal.target) return
    // 모달은 열린 채로 유지 — running 상태로 전환되어 '중지' 버튼 노출.
    // 편집된 system prompt 가 있으면 서버에 override 로 전달.
    const opts = { systemPromptOverride: editedSystem }
    if (llmModal.invalidateAfter) hook.rerunFromStage(llmModal.target, opts)
    else hook.runNextStage(opts)
  }

  // 실행이 시작됐다가 끝난 순간을 감지해 모달 자동 닫기.
  const wasRunningRef = useRef(false)
  useEffect(() => {
    if (!llmModal.open || !llmModal.target) {
      wasRunningRef.current = false
      return
    }
    if (hook.running === llmModal.target) {
      wasRunningRef.current = true
    } else if (wasRunningRef.current && hook.running === null) {
      wasRunningRef.current = false
      setLlmModal({ open: false, target: null, invalidateAfter: false, preview: null })
    }
  }, [hook.running, llmModal.open, llmModal.target])

  const onCommit = async () => {
    setCommitting(true)
    const result = await hook.commit()
    setCommitting(false)
    if (result) navigate(`/wedding/templates/${result.templateId}`, { replace: true })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* 상단 헤더 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e5e5e5',
          background: '#fff',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 11, color: '#999', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Stepwise import · session {session.sessionId.slice(0, 8)}
          </div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {session.name} <span style={{ color: '#999', fontWeight: 400 }}>({session.slug})</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {/* LLM 실행 옵션 — 언제든 변경 가능. 다음 LLM stage 실행 시 반영. */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              background: hook.skipLlm ? '#fff7e6' : '#e6f7ff',
              border: `1px solid ${hook.skipLlm ? '#ffd591' : '#91d5ff'}`,
              borderRadius: 4,
              fontSize: 12,
              cursor: 'pointer',
              userSelect: 'none',
            }}
            title="LLM 바인딩 패스를 건너뛸지 여부 — 다음 'LLM Bind' 실행에 반영됨"
          >
            <input
              type="checkbox"
              checked={hook.skipLlm}
              onChange={(e) => hook.setSkipLlm(e.target.checked)}
              style={{ margin: 0 }}
            />
            <span style={{ fontWeight: 500 }}>skipLlm</span>
            <span style={{ color: hook.skipLlm ? '#d48806' : '#0070c0' }}>
              {hook.skipLlm ? 'ON (LLM 건너뜀)' : 'OFF (LLM 실행)'}
            </span>
          </label>
          <Button variant="outline" size="sm" onClick={() => navigate('/wedding/templates')}>
            취소
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onCommit}
            disabled={!hook.canCommit || committing}
          >
            {committing ? <><Spinner size="sm" /> 저장 중...</> : '최종 저장'}
          </Button>
        </div>
      </div>

      {/* 본문: 왼쪽 StageList, 오른쪽 탭 뷰 */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <StageList
          status={hook.status}
          selected={selected}
          onSelect={hook.selectStage}
          isEdited={hook.isEdited}
        />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* 선택된 stage 헤더 */}
          <div
            style={{
              padding: '10px 16px',
              borderBottom: '1px solid #e5e5e5',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {STAGE_META[selected].label}
                <span style={{ marginLeft: 8, fontSize: 11, color: hook.status[selected] === 'error' ? '#f5222d' : '#999' }}>
                  {hook.status[selected]}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{STAGE_META[selected].description}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {hook.status[selected] !== 'idle' && hook.status[selected] !== 'invalidated' && (
                (() => {
                  const needsZip = selected === 'extract' || selected === 'upload'
                  const zipMissing = needsZip && !session.zipS3Key
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRerunClick}
                      disabled={hook.running !== null || zipMissing}
                      title={zipMissing ? '원본 ZIP 이 없어 이 단계 재실행은 불가' : undefined}
                    >
                      {hook.running === selected ? <><Spinner size="sm" /> 실행 중...</> : '이 단계부터 재실행'}
                    </Button>
                  )
                })()
              )}
              {hook.pendingNextStage && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => triggerNext()}
                  disabled={hook.running !== null}
                >
                  {hook.running === hook.pendingNextStage ? (
                    <><Spinner size="sm" /> 실행 중...</>
                  ) : (
                    <>다음 단계 실행 ({STAGE_META[hook.pendingNextStage].label})</>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* 탭 바 */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e5e5', background: '#fff' }}>
            {(['html', 'diff', 'preview', 'meta', 'debug'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: 'transparent',
                  borderBottom: tab === t ? '2px solid #1890ff' : '2px solid transparent',
                  color: tab === t ? '#1890ff' : '#555',
                  fontSize: 13,
                  fontWeight: tab === t ? 600 : 400,
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {t}
              </button>
            ))}
          </div>

          {/* 탭 본문 */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {tab === 'html' && (
              <StageHtmlEditor
                stage={selected}
                value={html}
                onChange={(next) => hook.editHtml(selected, next)}
                readOnly={!snapshot /* 아직 실행되지 않은 stage 는 read-only */}
              />
            )}
            {tab === 'diff' && (
              <StageDiffView
                before={prevHtml}
                after={html}
                beforeLabel={prev ? STAGE_META[prev].label : '(빈 입력)'}
                afterLabel={`${STAGE_META[selected].label}${hook.isEdited(selected) ? ' · edited' : ''}`}
              />
            )}
            {tab === 'preview' && <StagePreview html={html} />}
            {tab === 'meta' && <StageMetaPanel stage={selected} snapshot={snapshot} />}
            {tab === 'debug' && (
              <StageDebugPanel snapshot={snapshot} html={html} edited={hook.isEdited(selected)} />
            )}
          </div>
        </div>
      </div>

      {/* 재실행 확인 다이얼로그 */}
      <ConfirmDialog
        open={confirmRerun !== null}
        onClose={() => setConfirmRerun(null)}
        onConfirm={() => {
          if (confirmRerun) triggerRerun(confirmRerun)
          setConfirmRerun(null)
        }}
        title="이 단계부터 재실행"
        message={`'${confirmRerun ? STAGE_META[confirmRerun].label : ''}' 단계부터 다시 실행합니다. 이후 단계의 결과와 편집은 모두 사라집니다. 계속할까요?`}
        confirmLabel="재실행"
        variant="danger"
      />

      {/* LLM payload 미리보기 + 실행/중지 모달 */}
      <LlmPayloadModal
        open={llmModal.open}
        preview={llmModal.preview}
        running={llmModal.target !== null && hook.running === llmModal.target}
        onConfirm={onLlmConfirm}
        onCancel={() => setLlmModal({ open: false, target: null, invalidateAfter: false, preview: null })}
        onStop={() => hook.cancelRunningStage()}
        llmModel={hook.llmModel}
        onModelChange={onLlmModelChange}
        textModels={textModels}
      />
    </div>
  )
}
